/* eslint-disable max-len */
import axios from "axios";
import { API_URL } from "../config/app";
import { CLIENT_ID } from "./clientId";
import { invalidateForPath } from "./mutationInvalidator";
import { autoReportError } from "./errorReport";
import { TOKEN_KEY, ID_AZIENDA_KEY, LOGOUT_MSG_KEY, QUERY_CACHE_KEY } from "./storageKeys";

/**
 * Client HTTP unico dell'app (porting di gestionale-gls/src/core/api.jsx).
 *
 *  - aggiunge token, azienda e X-Client-Id a ogni richiesta;
 *  - rinnova in silenzio i token stantii o scaduti (TOKEN_STALE /
 *    TOKEN_EXPIRED) e ritenta UNA volta la richiesta;
 *  - un 401 vero chiude la sessione, un 403 apre il modale "operazione non
 *    consentita" e spedisce in automatico il report;
 *  - limiti del piano (402: dipendenti, esportazioni, spazio) e file troppo
 *    grandi (413) aprono il modale che spiega cosa fare (PlanLimitModal);
 *  - dopo ogni mutazione riuscita invalida subito la cache locale.
 */

let lastApiError = null;
export const getLastApiError = () => lastApiError;

export function captureApiError(error) {
  try {
    const cfg = (error && error.config) || {};
    let payload = cfg.data;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch {
        /* resta stringa */
      }
    }
    if (typeof FormData !== "undefined" && payload instanceof FormData) payload = "[multipart]";
    let kind = "response";
    if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) kind = "timeout";
    else if (!error?.response) kind = "network";
    lastApiError = {
      url: /^https?:\/\//i.test(cfg.url || "") ? cfg.url : `${cfg.baseURL || ""}${cfg.url || ""}`,
      method: (cfg.method || "").toUpperCase(),
      kind,
      requestPayload: payload ?? null,
      requestParams: cfg.params ?? null,
      status: error?.response?.status ?? null,
      responseData: error?.response?.data ?? null,
      message: error?.message || null,
      timestamp: new Date().toISOString(),
    };
  } catch {
    /* best-effort */
  }
}

/**
 * Messaggio da mostrare all'utente per un errore axios. Usarlo SEMPRE nei
 * catch, con un fallback specifico dell'operazione: il server dice già cosa
 * non va ("Il campo 'titolo' è obbligatorio") e buttarlo via per un testo
 * fisso toglie all'utente l'unico modo di correggersi da solo.
 */
export function getApiErrorMessage(error, fallback = "Operazione non riuscita.") {
  if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
    return "Il server non ha risposto in tempo. Ricarica la pagina e verifica se l'operazione è stata salvata prima di riprovare.";
  }
  if (!error?.response) return "Connessione al server non disponibile. Controlla che il backend sia avviato e riprova.";
  const data = error.response.data;
  if (typeof data === "string" && data.trim() && data.length < 300) return data.trim();
  if (data && typeof data === "object") {
    const msg = data.message || data.error;
    if (typeof msg === "string" && msg.trim()) return msg.trim();
  }
  if (error.response.status === 503) return "Servizio temporaneamente non disponibile. Riprova tra qualche istante.";
  return fallback;
}

const api = axios.create({ baseURL: API_URL, timeout: 28000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = token;
  const idAzienda = localStorage.getItem(ID_AZIENDA_KEY);
  if (idAzienda) config.headers["X-Azienda-Id"] = idAzienda;
  config.headers["X-Client-Id"] = CLIENT_ID;
  // Gli upload possono durare: niente timeout breve.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) config.timeout = 0;
  return config;
});

function extractMessage(error) {
  const data = error?.response?.data;
  if (!data) return null;
  if (typeof data === "string") return /token is required/i.test(data) ? null : data;
  return data.message || data.error || null;
}

const isTokenMissing = (error) => typeof error?.response?.data === "string" && /token is required/i.test(error.response.data);

function handleForbidden(error) {
  const cfg = error?.config || {};
  const serverMessage = extractMessage(error);
  const reference = autoReportError({
    signature: `${(cfg.method || "").toUpperCase()} ${cfg.url || ""} ${serverMessage || ""}`,
    modalText: `403 - operazione non autorizzata: ${serverMessage || "nessun messaggio dal server"}`,
    apiError: getLastApiError(),
  });
  window.dispatchEvent(new CustomEvent("pr:permission-denied", { detail: { reference, message: serverMessage } }));
}

const REFRESHABLE = ["TOKEN_STALE", "TOKEN_EXPIRED"];
const isRefreshable = (error) => error?.response?.status === 401 && REFRESHABLE.includes(error?.response?.data?.error);

function markRefreshError(error, message) {
  const err = error instanceof Error ? error : new Error(message || "refresh failed");
  const status = error?.response?.status;
  err.prFatal = status === 401 || status === 403 || status === 404 || Boolean(message);
  return err;
}

const isTransient = (error) => error?.code === "ECONNABORTED" || !error?.response || error.response.status === 429 || error.response.status >= 500;

let refreshPromise = null;
async function refreshTokenOnce() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const oldToken = localStorage.getItem(TOKEN_KEY);
      if (!oldToken) throw markRefreshError(null, "no token");
      const doPost = () => axios.post(`${API_URL}/refreshUserToken`, {}, { headers: { Authorization: oldToken }, timeout: 15000 });
      let res;
      try {
        res = await doPost();
      } catch (first) {
        if (!isTransient(first)) throw markRefreshError(first);
        await new Promise((r) => setTimeout(r, 1500));
        try {
          res = await doPost();
        } catch (second) {
          throw markRefreshError(second);
        }
      }
      const newToken = res?.data?.token;
      if (!newToken) throw markRefreshError(null, "no token in refresh response");
      localStorage.setItem(TOKEN_KEY, newToken);
      if (res.data.user?.idAzienda) localStorage.setItem(ID_AZIENDA_KEY, String(res.data.user.idAzienda));
      try {
        const { refreshSocketAuth } = await import("./socket");
        refreshSocketAuth();
      } catch {
        /* il realtime si riallinea alla prossima connessione */
      }
      return newToken;
    } finally {
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();
  return refreshPromise;
}

// Pagine raggiungibili senza sessione: da qui non si rimanda mai al login,
// altrimenti una chiamata senza token sul login stesso ricarica la pagina
// all'infinito.
const PUBLIC_PATH_RE = /^\/(login|invito|p\/|reset-password|unsubscribe|disiscrivi-cliente|pending-approval)/i;

function forceLogout(message) {
  const hadSession = Boolean(localStorage.getItem(TOKEN_KEY));
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(QUERY_CACHE_KEY);
  if (!hadSession || PUBLIC_PATH_RE.test(window.location.pathname)) return;
  localStorage.setItem(LOGOUT_MSG_KEY, message);
  window.location.href = "/Login";
}

api.interceptors.response.use(
  (response) => {
    try {
      invalidateForPath(response.config?.method, response.config?.url);
    } catch (_) {
      /* mai bloccare una risposta riuscita */
    }
    return response;
  },
  async (error) => {
    captureApiError(error);
    const status = error.response?.status;

    if (isRefreshable(error) && !error.config?._retriedAfterRefresh) {
      try {
        const newToken = await refreshTokenOnce();
        const cfg = error.config || {};
        cfg._retriedAfterRefresh = true;
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = newToken;
        try {
          const { fetchUserConfig } = await import("../utils/roleHelper");
          await fetchUserConfig(true);
        } catch {
          /* best-effort */
        }
        return api.request(cfg);
      } catch (refreshErr) {
        if (refreshErr?.prFatal) forceLogout(refreshErr?.response?.data?.message || "Sessione scaduta. Accedi di nuovo.");
        return Promise.reject(refreshErr);
      }
    }

    if ((status === 401 && !isRefreshable(error)) || (status === 403 && isTokenMissing(error))) {
      forceLogout("Sessione scaduta. Accedi di nuovo.");
    } else if (status === 403) {
      handleForbidden(error);
    } else if ((status === 402 || status === 413) && error.response?.data?.error) {
      const d = error.response.data;
      if (["STORAGE_QUOTA_EXCEEDED", "FILE_TOO_LARGE", "PLAN_LIMIT_DIPENDENTI", "PLAN_LIMIT_EXPORT"].includes(d.error)) {
        window.dispatchEvent(new CustomEvent("pr:piano", { detail: d }));
      } else if (d.error === "SUBSCRIPTION_REQUIRED") {
        // Studio senza piano: AppLayout rilegge il piano e mostra la sospensione.
        window.dispatchEvent(new CustomEvent("pr:sospeso"));
      }
    }
    return Promise.reject(error);
  },
);

export const forceTokenRefresh = refreshTokenOnce;

export function getTokenExpiryMs(token = localStorage.getItem(TOKEN_KEY)) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const exp = Number(JSON.parse(atob(payload)).exp);
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
  } catch {
    return null;
  }
}

// Rinnovo preventivo a meno di 24 ore dalla scadenza: senza, il socket si
// riconnetterebbe all'infinito con un token scaduto (vedi gestionale-gls).
export async function refreshTokenIfExpiringSoon() {
  const expMs = getTokenExpiryMs();
  if (!expMs || expMs - Date.now() > 24 * 60 * 60 * 1000) return;
  try {
    await refreshTokenOnce();
  } catch {
    /* ci pensa l'interceptor alla prossima chiamata */
  }
}

export default api;
